import {
  Autocomplete,
  Chip,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import {
  ChartsGrid,
  LineChartPro,
  lineElementClasses,
  markElementClasses,
  MarkPlot,
} from "@mui/x-charts-pro";
import { LinePlot} from "@mui/x-charts-pro";
import { CustomItemTooltip } from "../../../../../ResuableChartTooltip/CustomItemTooltip";
import { ChartSkeleton } from "../../../../../ChartSkeleton";
import { useState, useMemo, useEffect} from "react";
import { useSearchParams } from "react-router-dom";

interface ImpressionShareData {
  matchtype: string;
  display_url_domain: string;
  avg_impshare: number;
  kw_name: string;
  activity_date: string;
  account_source: string;
  kw_id: number;
}

interface CompetitorData {
  kw_name: string;
  activity_date: string;
  matchtype: string;
  account_source: string;
  num_competitors: number;
  kw_id: number;
}

interface KeywordOption {
  label: string;
}

const ImpressionShare = ({
  impressionShare1,
  impressionShare2,
}: {
  impressionShare1: { query_output: ImpressionShareData[] };
  impressionShare2: { query_output: CompetitorData[] };
}) => {
  const [selectedKeyword, setSelectedKeyword] = useState<KeywordOption | null>({ label: "All" });
  const [isLoading, setIsLoading] = useState(false);
  const [showMarks] = useState(false);
  const [searchParams] = useSearchParams();
  const insightType = searchParams.get("insightType");
  const [selectedCompetitors, setSelectedCompetitors] = useState([]) as any;

  const processedData = useMemo(() => {
    const uniqueKeywords = [
      ...new Set(
        impressionShare1?.query_output?.map((item) => item.kw_name) || []
      ),
    ];
    const keywords = [
      { label: "All" },
      ...uniqueKeywords.map((kw) => ({ label: kw })),
    ];

    const filteredImpShare =
      !selectedKeyword || selectedKeyword.label === "All"
        ? impressionShare1?.query_output
        : impressionShare1?.query_output.filter(
            (item) => item.kw_name === selectedKeyword.label
          );

    const filteredCompetitors =
      !selectedKeyword || selectedKeyword.label === "All"
        ? impressionShare2?.query_output
        : impressionShare2?.query_output.filter(
            (item) => item.kw_name === selectedKeyword.label
          );

    // Get unique competitors
    const uniqueCompetitors = [
      ...new Set(filteredImpShare?.map((item) => item.display_url_domain)),
    ];

    // Defensive: handle undefined/null filteredImpShare
    if (!Array.isArray(filteredImpShare) || filteredImpShare.length === 0) {
      return {
        keywords,
        chartData: [],
        competitors: uniqueCompetitors,
      };
    }

    // Group and process data by date
    const processedDataPoints = Object.values(
      filteredImpShare.reduce((acc: any, curr) => {
        const date = curr.activity_date;
        if (!acc[date]) {
          acc[date] = {
            date,
            num_competitors: 0,
            ...uniqueCompetitors?.reduce(
              (obj, comp) => ({ ...obj, [comp]: 0 }),
              {}
            ),
          };
        }

        acc[date][curr.display_url_domain] = curr.avg_impshare;

        // Add competitor count from impressionShare2
        const competitorInfo = filteredCompetitors?.find(
          (c) => c.activity_date === date
        );
        if (competitorInfo) {
          acc[date].num_competitors = competitorInfo.num_competitors;
        }

        return acc;
      }, {})
    ) as any;

    // Sort by date ascending
    const sortedDataPoints = processedDataPoints.sort(
      (a: any, b: any) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return {
      keywords,
      chartData: sortedDataPoints,
      competitors: uniqueCompetitors,
    };
  }, [
    impressionShare1?.query_output,
    impressionShare2?.query_output,
    selectedKeyword,
  ]);

  useEffect(() => {
  if (processedData?.competitors?.length > 0) {
    // Only preselect defaults when there is no current selection (avoid overwriting user choices)
    if (!selectedCompetitors || selectedCompetitors.length === 0) {
      const filteredCompetitors = processedData.competitors.filter(
          (comp: string) => comp !== "You"
      );
      setSelectedCompetitors(["You", ...filteredCompetitors.slice(0, 5)]);
    }
  }}, [processedData, selectedCompetitors]);
  const avgCompetitors = useMemo(() => {
    const competitors = impressionShare2?.query_output || [];
    return Math.round(
      competitors.reduce((sum, curr) => sum + curr.num_competitors, 0) /
        competitors.length
    );
  }, [impressionShare2?.query_output]);

    // Update the series useMemo to filter based on selectedCompetitors and maintain consistent colors
  const series: any = useMemo(() => {
    const generateBrightColor = (index: number, total: number, opacity = 1) => {
      const hue = (index * (360 / total)) % 360; // Distribute hues evenly
      return `hsl(${hue}, 80%, 60%, ${opacity})`; // High saturation and lightness for bright colors
    };

    // Always include "You" series
    const youSeries = {
        id: "you",
        type: "line" as const,
        dataKey: "You",
        color: "#6200EE",
        curve: "linear",
        label: "Optival",
        strokeWidth: 4,
    };

    // If no competitors selected, return only "You"
    if (!selectedCompetitors || selectedCompetitors.length === 0) {
        return [youSeries];
    }

    // Filter competitors based on selection (exclude "You" as it's always shown)
    const filteredCompetitors = (selectedCompetitors || []).filter((c: string) => c !== "You");

    // Use the original competitors array for consistent color indexing
    const competitorLines = filteredCompetitors.map((comp: string) => {
        const originalIndex = processedData.competitors.indexOf(comp);
        return {
            id: `competitor-${originalIndex}`,
            type: "line" as const,
            dataKey: comp,
            label: comp,
            color: generateBrightColor(originalIndex, processedData.competitors.length),
            curve: "linear",
        };
    });

    return [youSeries, ...competitorLines];
  }, [selectedCompetitors, processedData.competitors]);

  // Build a map of dataKey -> color so tooltips can use the exact same colors as chart series
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    (series || []).forEach((s: any) => {
      if (s && s.dataKey && s.color) {
        map[s.dataKey] = s.color;
      }
    });
    return map;
  }, [series]);

  // Calculate min/max values from chart data
  const yAxisRange = useMemo(() => {
    if (!processedData.chartData.length) {
      return { min: 0, max: 100 };
    }

    const allValues: number[] = [];

    // Collect all impression share values from all competitors including "You"
    processedData.chartData.forEach((dataPoint:any) => {
      processedData.competitors.forEach(competitor => {
        const value = dataPoint[competitor];
        if (typeof value === 'number' && !isNaN(value)) {
          allValues.push(value);
        }
      });
    });

    if (allValues.length === 0) {
      return { min: 0, max: 100 };
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // Add some padding (5% of range) to make the chart more readable
    const range = max - min;
    const padding = range * 0.05;

    return {
      min: Math.max(0, Math.floor(min - padding)), // Don't go below 0
      max: Math.min(100, Math.ceil(max + padding)) // Don't go above 100 for percentage values
    };
  }, [processedData.chartData, processedData.competitors]);

  const handleKeywordChange = (_: any, newValue: { label: string } | null) => {
    setIsLoading(true);
    setSelectedKeyword(newValue);

    // If the keyword field is cleared, ensure competitors show only "You"
    if (newValue === null) {
      setSelectedCompetitors(["You"]);
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  if (isLoading) {
    return <ChartSkeleton />;
  }

  return (
    <Paper sx={{ marginTop: "8px" }} elevation={1}>
      <Stack
        flexDirection={"row"}
        justifyContent="space-between"
        alignItems={"center"}
        padding={2}
      >
        <Stack
          direction="column"
          justifyContent="space-between"
          alignItems="left"
        >
          <Typography fontSize={18}>Impression Share</Typography>
          <Typography fontSize={14} fontWeight={500}>
            {impressionShare2?.query_output?.length > 0 && (
              <>Average number of competitors: {avgCompetitors}</>
            )}
          </Typography>
        </Stack>
        <Autocomplete
          size="small"
          disablePortal
          disableClearable
          options={processedData.keywords.filter(
            (option) => option.label !== selectedKeyword?.label
          )}
          data-tracker={insightType === "adgroup" ? "309" : "330"}
          value={selectedKeyword ?? undefined}
          onChange={handleKeywordChange}
          sx={{ width: 272 }}
          renderInput={(params) => (
            <TextField
              color="secondary"
              {...params}
              label="Keyword"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ marginLeft: "12px" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Stack>
      <Box>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          disableCloseOnSelect // ✅ keeps menu open while selecting multiple
          options={(() => {
            const nonYou = (processedData?.competitors || []).filter(
              (c: string) => c !== "You"
            );
            const isAllSelected = selectedCompetitors.length === nonYou.length + 1; // +1 for 'You'
            return [isAllSelected ? "Deselect All" : "Select All", "You", ...nonYou];
          })()}
          getOptionLabel={(option) => (option === "You" ? "Optival" : String(option))}
          onChange={(_event, newValue) => {
            const allCompetitors = processedData.competitors || [];
            const nonYou = allCompetitors.filter((c: string) => c !== "You");

            if (newValue.includes("Select All")) {
              setSelectedCompetitors(["You", ...nonYou]);
            } else if (newValue.includes("Deselect All")) {
              setSelectedCompetitors(["You"]);
            } else {
              // filter out special markers and ensure 'You' is always present
              const cleaned = newValue.filter((v: string) => v !== "Select All" && v !== "Deselect All");
              const ensured = Array.from(new Set(["You", ...cleaned]));
              setSelectedCompetitors(ensured as any);
            }
          }}
          value={selectedCompetitors}
          sx={{ width: "100%", padding: "8px 16px" }}
          renderOption={(props, option, { selected }) => {
            const allCompetitors = (processedData.competitors || []).filter((comp: string) => comp !== "You");
            const isSelectAll = option === "Select All";
            const isDeselectAll = option === "Deselect All";
            if (option === "You") {
              return (
                <li {...props} style={{ fontWeight: 600, borderBottom: "1px solid #eee" }}>
                  <Checkbox size="small" checked color="secondary" disabled sx={{ mr: 1 }} />
                  <span style={{ color: "#6b46c1", fontWeight: 600 }}>Optival</span>
                </li>
              );
            }

            const isChecked = isSelectAll || isDeselectAll ? selectedCompetitors.length === allCompetitors.length + 1 : selected;
            return (
              <li
                {...props}
                style={
                  isSelectAll || isDeselectAll
                    ? {
                      fontWeight: 600,
                      backgroundColor: "#f7f7f7",
                      borderBottom: "1px solid #ddd",
                    }
                    : {}
                }
              >
                <Checkbox size="small" checked={isChecked} color="secondary" sx={{ mr: 1 }} />
                {String(option)}
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              if (option === "You") {
                return (
                  <Chip
                    key={option}
                    label={"Optival"}
                    color="secondary"
                    size="small"
                    variant="filled"
                    sx={{
                      backgroundColor: "#fff",
                      color: "secondary.main",
                      border: "1px solid rgba(0,0,0,0.12)",
                      fontWeight: 600,
                    }}
                  />
                );
              }

              return (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  color="secondary"
                  size="small"
                  variant="filled"
                />
              );
            })
          }
          renderInput={(params) => (
              <TextField
                  color="secondary"
                  {...params}
                  label="Competitors"
                  slotProps={{
                      inputLabel: {
                          shrink: true,
                      },
                  }}
                  InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                          <>
                              <InputAdornment position="start">
                                  <SearchIcon sx={{ marginLeft: "12px" }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                          </>
                      ),
                  }}
              />
          )}
        />
        <Stack sx={{ width: "100%" }}>
          {processedData.chartData.length === 0 ? (
            <Box
              sx={{
                height: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 400, fontSize: 16 }}>
                No Data to Display
              </Typography>
            </Box>
          ) : (
            <LineChartPro
              series={series}
              xAxis={[
                {
                  scaleType: "band",
                  dataKey: "date",
                  valueFormatter: (date: string) => {
                    const d = new Date(date);
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const year = d.getFullYear();
                    return `${day}/${month}/${year}`;
                  },
                },
              ]}
              yAxis={[
                {
                  id: "leftAxis",
                  dataKey: "You",
                  min: yAxisRange.min,
                  max: yAxisRange.max,
                  valueFormatter: (v: number) => `${v}%`,
                },
                { id: "rightAxis", dataKey: "num_competitors" },
              ]}
              dataset={processedData.chartData}
              height={400}
              margin={{ left: 48, right: 56, top: 25, bottom: 84 }}
              leftAxis={null}
              rightAxis={{}}
              sx={{
                [`& .${lineElementClasses.root}.MuiLineElement-series-you`]: {
                  strokeWidth: 3,
                  opacity: 1,
                },
                // All other lines
                [`& .${lineElementClasses.root}:not(.MuiLineElement-series-you)`]:
                  {
                    strokeWidth: 2,
                    opacity: 0.2,
                    "&:hover": {
                      strokeWidth: 3,
                      opacity: 1,
                    },
                  },
                [`& .MuiChartsAxis-tickLabel`]: {
                  fill: "grey !important",
                },
                [`& .MuiChartsLegend-root text`]: {
                  fill: "grey !important",
                },
                [`& .${markElementClasses.root}`]: {
                  opacity: showMarks ? 1 : 0,
                  scale: showMarks ? "1" : "0.6",
                  strokeWidth: 2,
                  transition: "opacity 0.2s ease, scale 0.2s ease",
                  "&:hover": {
                    scale: "1.2",
                    strokeWidth: 5,
                    opacity: 0.8,
                  },
                },

                // ✅ Hide the right Y-axis line
                "& .MuiChartsAxis-right .MuiChartsAxis-line": {
                  stroke: "none !important",
                },

                // ✅ Hide the right Y-axis ticks
                "& .MuiChartsAxis-tick": {
                  stroke: "none !important",
                },

                // ✅ Optionally hide the right Y-axis labels too
                "& .MuiChartsAxis-line": {
                  display: "none",
                },
              }}
              slotProps={{
                legend: {
                  direction: "row",
                  position: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  labelStyle: {
                    fontSize: 12,
                  },
                  itemMarkWidth: 12,
                  itemMarkHeight: 2,
                  markGap: 8,
                  itemGap: 10,
                  padding: 28,
                },
              }}
            >
              <ChartsGrid horizontal />
              <CustomItemTooltip dataset={processedData.chartData} colorMap={colorMap} />
              <LinePlot />
              <MarkPlot />
            </LineChartPro>
          )}
        </Stack>
      </Box>{" "}
    </Paper>
  );
};

export default ImpressionShare;
